import { Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, Skeleton, styled, TextField, Tooltip, Typography } from '@mui/material';

import type { FieldMatchingFormValues } from './types';

// ----------------------------------------------------------------------

type RHFFieldsMatchingAutocompleteProps = AutocompleteProps<string | null, false, false, false>;

export type RHFFieldsMatchingOptionsFieldProps = Omit<RHFFieldsMatchingAutocompleteProps, 'renderInput'> & {
  name: `columns.${string}`;
  isInitialLoading: boolean;
  titleByKey: Record<string, string>;
  selectedOptions: Array<string | undefined>;
  requiredOptions?: string[];
};

// ----------------------------------------------------------------------

const StyledTextField = styled(TextField)(({ theme }) => ({
  padding: theme.spacing(0, 1),

  '&& .MuiInputBase-root.MuiFilledInput-root': {
    '.MuiFilledInput-input': {
      height: 40,
      minHeight: 40,
    },
  },
})) as typeof TextField;

// ----------------------------------------------------------------------

export const RHFFieldsMatchingOptionsField = (props: RHFFieldsMatchingOptionsFieldProps) => {
  const {
    name,
    isInitialLoading,
    titleByKey,
    selectedOptions,
    getOptionLabel = option => (option ? titleByKey[option] : ''),
    getOptionDisabled = option => (option ? selectedOptions.includes(option) : false),
    renderOption,
    options,
    requiredOptions = [],
    ...restOfAutocompleteProps
  } = props;

  const { t } = useTranslation('field-matching');
  const { control } = useFormContext<FieldMatchingFormValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value = null, onChange, ...restOfFieldProps } }) => {
        const reassignedOnChange: RHFFieldsMatchingAutocompleteProps['onChange'] = (_, value) => {
          onChange(value);
        };

        return (
          <Fragment>
            {isInitialLoading && <Skeleton variant="text" width="100%" height={48} sx={{ transform: 'none' }} />}
            {!isInitialLoading && (
              <Autocomplete
                {...restOfFieldProps}
                fullWidth
                value={value}
                options={options}
                getOptionLabel={getOptionLabel}
                getOptionDisabled={getOptionDisabled}
                renderOption={
                  renderOption ??
                  ((props, option) =>
                    option && (
                      <Tooltip
                        key={option}
                        arrow
                        disableInteractive
                        title={
                          getOptionDisabled(option) ? (
                            <Typography component="p" variant="Body/small" color="neutral.500">
                              {t('field-matching-form.rhf-fields-matching-options-field.get-option-disabled')}
                            </Typography>
                          ) : (
                            ''
                          )
                        }
                      >
                        <div>
                          <li {...props}>
                            <Typography noWrap component="span" data-testid={`field-matching-select-option-${option}`}>
                              {titleByKey[option]}
                              {(requiredOptions.length === 0 || requiredOptions.includes(option)) && (
                                <Typography component="span" style={{ color: 'red' }}>
                                  {` *`}
                                </Typography>
                              )}
                            </Typography>
                          </li>
                        </div>
                      </Tooltip>
                    ))
                }
                renderInput={params => (
                  <StyledTextField
                    {...params}
                    variant="standard"
                    placeholder={t('field-matching-form.rhf-fields-matching-options-field.render-input-assign')}
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                    inputProps={{ ...params.inputProps, 'data-testid': 'field-matching-select-field' }}
                  />
                )}
                onChange={reassignedOnChange}
                noOptionsText=""
                slotProps={{
                  popper: {
                    sx: {
                      width: 'fit-content !important',
                    },
                  },
                }}
                {...restOfAutocompleteProps}
              />
            )}
          </Fragment>
        );
      }}
    />
  );
};
